import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const AifDetail = ({ fund }) => {
  const currentYear = new Date().getFullYear();
  const mainContentRef = useRef(null);

  useEffect(() => {
    document.title = `${fund.name} | Zephyr Peacock`;
    mainContentRef.current?.focus();

    return () => {
      document.title = 'Zephyr Peacock — Alternative Investment Funds';
    };
  }, [fund.name]);

  const isCatI = fund.category === 'Category I';

  return (
    <div className="scheme-page">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* ── White Site Header with Logo + Back Button ── */}
      <header className="site-header" role="banner">
        <div className="site-header-inner">
          <Link
            to="/"
            className="header-back"
            aria-label="Back to all funds"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M13 8H3M7 4L3 8l4 4" />
            </svg>
            All Funds
          </Link>

          <img
            src="/img/ZP logo.jpg"
            alt="Zephyr Peacock logo"
            className="site-logo"
          />
        </div>
      </header>

      {/* ── Teal Hero ── */}
      <section className="scheme-hero" aria-labelledby="scheme-title">
        <div className="scheme-hero-inner anim-fade-up">
          <span className="scheme-category-badge">
            {fund.category} AIF
          </span>
          <h1 id="scheme-title" className="scheme-hero-title">{fund.name}</h1>
          <p className="scheme-hero-sub">SEBI Registered Alternative Investment Fund</p>
        </div>
      </section>

      {/* ── Info Cards ── */}
      <main
        id="main-content"
        className="scheme-body"
        aria-labelledby="scheme-title"
        tabIndex={-1}
        ref={mainContentRef}
      >
        <div className="info-grid" role="list" aria-label={`${fund.name} disclosure details`}>

          {/* SEBI Registration */}
          <div className="info-card anim-fade-up anim-delay-1" role="listitem">
            <div className={`info-icon-wrap ${isCatI ? 'icon-green' : 'icon-cyan'}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <p className="info-label">SEBI Registration Number</p>
            <p className="info-value sebi-code">{fund.sebi}</p>
            <p className="info-sub">SEBI registered under the AIF Regulations</p>
          </div>

          {/* Scores ID */}
          <div className="info-card anim-fade-up anim-delay-2" role="listitem">
            <div className={`info-icon-wrap ${isCatI ? 'icon-forest' : 'icon-teal'}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
            </div>
            <p className="info-label">Scores ID</p>
            <p className="info-value sebi-code">{fund.scoresId}</p>
          </div>

          {/* Investment Manager */}
          <div className="info-card anim-fade-up anim-delay-3" role="listitem">
            <div className="info-icon-wrap icon-teal" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                <line x1="12" y1="12" x2="12" y2="16" />
                <line x1="10" y1="14" x2="14" y2="14" />
              </svg>
            </div>
            <p className="info-label">Investment Manager</p>
            <p className="info-value">{fund.investmentManager}</p>
          </div>

          {/* Category */}
          <div className="info-card anim-fade-up anim-delay-4" role="listitem">
            <div className={`info-icon-wrap ${isCatI ? 'icon-forest' : 'icon-teal'}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <p className="info-label">Category of AIF</p>
            <p className="info-value">{fund.category}</p>
            <p className="info-sub">
              {isCatI
                ? 'Venture capital, infrastructure, social venture, SME funds'
                : 'Private equity, debt, real estate, distressed asset funds'}
            </p>
          </div>

          {/* Compliance Officer */}
          <div className="info-card anim-fade-up anim-delay-5" role="listitem">
            <div className="info-icon-wrap icon-teal" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <p className="info-label">Compliance Officer</p>
            <p className="info-value">Nikhil Sultania</p>
          </div>

        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="site-footer" role="contentinfo">
        <p>Compliance Officer — Nikhil Sultania</p>
        <p>© {currentYear} Zephyr Peacock. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AifDetail;
